<script setup lang="ts">
/*
 * KPI tile from the prototype: label / large value / optional delta.
 * Used heavily on Pay center, Step KPI strips, and the Home dashboard.
 *
 * `size` scales the value text ('md' = prototype default text-2xl; 'sm' =
 * text-xl for denser summary strips). `valueTone` lets a single card surface
 * its value in danger red (e.g. orphan grades) without affecting label/delta.
 * The `#label` slot overrides the plain `label` prop for rich/faded sub-text;
 * it falls back to the prop so existing callers stay byte-identical.
 *
 * Marker classes (`.kpi` on root, `.lbl` on label, `.val` on value) match the
 * proto's class names so the redesign audit driver (which selects
 * `.kpi .lbl` to enumerate KPI strips) picks up KpiCard tiles wherever they
 * are used. The proto's `.kpi` rule in `src/app-elements.css` overrides our
 * Tailwind background/border/radius/padding, which is the desired outcome —
 * it aligns the visible chrome with the prototype.
 */
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    label: string;
    value: string | number;
    delta?: string;
    trend?: 'up' | 'down' | 'flat';
    /** Value text size: 'md' = text-2xl (default), 'sm' = text-xl. */
    size?: 'md' | 'sm';
    /** 'danger' renders the value in red; 'default' leaves it unchanged. */
    valueTone?: 'default' | 'danger';
  }>(),
  { trend: 'flat', size: 'md', valueTone: 'default' },
);

const deltaClass = computed(() =>
  props.trend === 'up'
    ? 'text-emerald-700'
    : props.trend === 'down'
      ? 'text-red-700'
      : 'text-app-muted',
);

const valueSizeClass = computed(() => (props.size === 'sm' ? 'text-xl' : 'text-2xl'));
const valueToneClass = computed(() =>
  props.valueTone === 'danger' ? 'text-red-600' : 'text-app-fg',
);
</script>

<template>
  <!--
    Semantic `<dl>` so screen readers announce label + value as a
    term/description pair ("Pending — 5") rather than two adjacent
    sentences. Wave 3.3 a11y; visual output is unchanged.
  -->
  <dl class="kpi rounded-3xl border border-app-border bg-app-surface px-5 py-4">
    <dt class="lbl text-sm font-medium text-app-muted">
      <slot name="label">{{ label }}</slot>
    </dt>
    <dd :class="['val mt-2 font-bold leading-none tracking-tight', valueSizeClass, valueToneClass]">
      <slot name="value">{{ value }}</slot>
    </dd>
    <dd v-if="delta" :class="['mt-1.5 text-sm font-medium', deltaClass]">{{ delta }}</dd>
  </dl>
</template>
